import React, { useState, useCallback } from 'react';
import {
  collectUnlinkedInputs,
  autoLinkInputs,
  applyProposals,
  CONFIDENCE_LABELS,
} from '../services/traciAutoLinker';

/**
 * AutoLinkPanel — TRACI score auto-linker (Beta)
 *
 * Shown inside the LCA tab when a project has no baked-in TRACI scores.
 * Runs a two-step match: TRACI DB keyword search + Gemini disambiguation.
 * User reviews proposals and accepts/rejects before applying to the project.
 */
export default function AutoLinkPanel({ project, onProjectUpdated }) {
  const datasetLabel = impactSourceLabel(getActiveImpactSource(project));
  const [phase, setPhase] = useState('idle'); // idle | running | review | done
  const [progress, setProgress] = useState({ current: 0, total: 0, name: '' });
  const [proposals, setProposals] = useState([]);
  const [error, setError] = useState(null);

  const unlinkedCount = collectUnlinkedInputs(project).length;

  const handleRun = useCallback(async () => {
    setError(null);
    setPhase('running');
    setProgress({ current: 0, total: 0, name: '' });
    try {
      const unlinked = collectUnlinkedInputs(project);
      if (unlinked.length === 0) {
        setPhase('done');
        return;
      }
      const results = await autoLinkInputs(unlinked, true, (cur, total, name) => {
        setProgress({ current: cur + 1, total, name });
      });
      setProposals(results);
      setPhase('review');
    } catch (err) {
      setError(err.message);
      setPhase('idle');
    }
  }, [project]);

  const toggleAccept = (idx) => {
    setProposals(prev => prev.map((p, i) => i === idx ? { ...p, accepted: !p.accepted } : p));
  };

  const swapCandidate = (proposalIdx, candidateIdx) => {
    setProposals(prev => prev.map((p, i) => {
      if (i !== proposalIdx) return p;
      return { ...p, bestMatch: p.candidates[candidateIdx], accepted: true };
    }));
  };

  const handleApply = useCallback(() => {
    const updatedProject = applyProposals(project, proposals);
    onProjectUpdated(updatedProject);
    setPhase('done');
  }, [project, proposals, onProjectUpdated]);

  const acceptedCount = proposals.filter(p => p.accepted && p.bestMatch).length;

  // -- Idle state -------------------------------------------------------
  if (phase === 'idle') {
    return (
      <div style={{
        margin: '16px 12px', padding: '16px', borderRadius: '10px',
        border: '1.5px solid #e8f0fe', background: '#f8faff',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: '14px', color: '#1a1a1a', marginBottom: '4px' }}>
              Auto-Link TRACI Scores
              <sup style={{ fontSize: '8px', fontWeight: 400, color: '#888', letterSpacing: '0.3px', marginLeft: '4px', opacity: 0.7 }}>BETA</sup>
            </div>
            <div style={{ fontSize: '12.5px', color: '#666', lineHeight: 1.5 }}>
              This project has <strong>{unlinkedCount} unlinked input{unlinkedCount !== 1 ? 's' : ''}</strong> with no environmental scores.
              Auto-linking searches the {datasetLabel} TRACI lookup and uses AI to propose the best match for each input.
              You review and approve before anything is applied.
            </div>
            {error && (
              <div style={{ marginTop: '8px', fontSize: '12px', color: '#c62828' }}>Error: {error}</div>
            )}
          </div>
        </div>
        <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
          <button
            onClick={handleRun}
            style={{
              background: '#1a73e8', border: 'none', color: '#fff',
              padding: '8px 20px', fontSize: '13px', fontWeight: 500,
              borderRadius: '7px', cursor: 'pointer',
            }}
          >
            Run Auto-Link
          </button>
          <span style={{ fontSize: '11.5px', color: '#aaa', alignSelf: 'center' }}>
            Searches {unlinkedCount} input{unlinkedCount !== 1 ? 's' : ''} · powered by {datasetLabel} + AI
          </span>
        </div>
      </div>
    );
  }

  // -- Running state ----------------------------------------------------
  if (phase === 'running') {
    const pct = progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0;
    return (
      <div style={{
        margin: '16px 12px', padding: '16px', borderRadius: '10px',
        border: '1.5px solid #e8f0fe', background: '#f8faff',
      }}>
        <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '8px', color: '#1a1a1a' }}>
          Auto-linking inputs…
        </div>
        <div style={{
          height: '6px', borderRadius: '3px', background: '#e0e0e0', overflow: 'hidden', marginBottom: '8px',
        }}>
          <div style={{
            height: '100%', borderRadius: '3px', background: '#1a73e8',
            width: `${pct}%`, transition: 'width 0.3s ease',
          }} />
        </div>
        <div style={{ fontSize: '12px', color: '#666' }}>
          {progress.current}/{progress.total} — {progress.name || '…'}
        </div>
      </div>
    );
  }

  // -- Done state -------------------------------------------------------
  if (phase === 'done') {
    return (
      <div style={{
        margin: '16px 12px', padding: '14px 16px', borderRadius: '10px',
        border: '1.5px solid #c8e6c9', background: '#f1f8e9',
        display: 'flex', alignItems: 'center', gap: '10px',
      }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2e7d32" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
        <span style={{ fontSize: '13px', color: '#2e7d32', fontWeight: 500 }}>
          TRACI scores applied — run LCA Analysis to see results.
        </span>
      </div>
    );
  }

  // -- Review state -----------------------------------------------------
  const highCount = proposals.filter(p => p.confidence === 'high').length;
  const medCount = proposals.filter(p => p.confidence === 'medium').length;
  const lowCount = proposals.filter(p => p.confidence === 'low').length;

  return (
    <div style={{ margin: '12px', borderRadius: '10px', border: '1.5px solid #e8f0fe', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{
        background: '#f8faff', padding: '12px 16px', borderBottom: '1px solid #e8f0fe',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px',
      }}>
        <div>
          <span style={{ fontWeight: 600, fontSize: '13.5px', color: '#1a1a1a' }}>
            Review Auto-Link Proposals
            <sup style={{ fontSize: '8px', fontWeight: 400, color: '#888', marginLeft: '4px', opacity: 0.7 }}>BETA</sup>
          </span>
          <div style={{ fontSize: '11.5px', color: '#666', marginTop: '2px' }}>
            <span style={{ color: CONFIDENCE_LABELS.high.color, fontWeight: 500 }}>{highCount} high</span>
            {' · '}
            <span style={{ color: CONFIDENCE_LABELS.medium.color, fontWeight: 500 }}>{medCount} medium</span>
            {' · '}
            <span style={{ color: CONFIDENCE_LABELS.low.color, fontWeight: 500 }}>{lowCount} low</span>
            {' confidence · '}
            <strong>{acceptedCount}</strong> selected
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => setProposals(prev => prev.map(p => ({ ...p, accepted: !!p.bestMatch })))}
            style={{
              background: 'none', border: '1px solid #ccc', borderRadius: '6px',
              padding: '5px 10px', fontSize: '12px', cursor: 'pointer', color: '#555',
            }}
          >
            Select All
          </button>
          <button
            onClick={handleApply}
            disabled={acceptedCount === 0}
            style={{
              background: acceptedCount > 0 ? '#1a73e8' : '#ccc', border: 'none', color: '#fff',
              padding: '6px 16px', fontSize: '13px', fontWeight: 500, borderRadius: '7px',
              cursor: acceptedCount > 0 ? 'pointer' : 'not-allowed',
            }}
          >
            Apply {acceptedCount} Link{acceptedCount !== 1 ? 's' : ''}
          </button>
        </div>
      </div>

      {/* Proposals list */}
      <div style={{ maxHeight: '380px', overflowY: 'auto' }}>
        {proposals.map((proposal, idx) => {
          const conf = CONFIDENCE_LABELS[proposal.confidence] || CONFIDENCE_LABELS.low;
          return (
            <div
              key={idx}
              style={{
                padding: '10px 16px', borderBottom: '1px solid #f0f0f0',
                background: proposal.accepted ? '#f8faff' : '#fff',
                display: 'flex', gap: '10px', alignItems: 'flex-start',
              }}
            >
              {/* Checkbox */}
              <input
                type="checkbox"
                checked={proposal.accepted && !!proposal.bestMatch}
                disabled={!proposal.bestMatch}
                onChange={() => toggleAccept(idx)}
                style={{ marginTop: '3px', flexShrink: 0, accentColor: '#1a73e8', cursor: 'pointer' }}
              />

              {/* Content */}
              <div style={{ flex: 1, minWidth: 0 }}>
                {/* Input name */}
                <div style={{ fontSize: '13px', fontWeight: 500, color: '#1a1a1a', marginBottom: '3px' }}>
                  {proposal.inputName}
                  <span style={{ fontSize: '11px', color: '#aaa', marginLeft: '6px', fontWeight: 400 }}>
                    {proposal.procName}{proposal.location ? ` · ${proposal.location}` : ''}
                  </span>
                </div>

                {proposal.bestMatch ? (
                  <>
                    {/* Best match */}
                    <div style={{ fontSize: '12px', color: '#333', marginBottom: '3px' }}>
                      <span style={{ color: '#888', marginRight: '4px' }}>→</span>
                      <strong>{proposal.bestMatch.reference_product || proposal.bestMatch.activity_name}</strong>
                      <span style={{ color: '#888', marginLeft: '4px', fontStyle: 'italic' }}>
                        ({proposal.bestMatch.activity_name}, {proposal.bestMatch.location})
                      </span>
                    </div>

                    {/* Confidence + reasoning */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{
                        fontSize: '10.5px', fontWeight: 600, color: conf.color,
                        background: `${conf.color}18`, borderRadius: '4px',
                        padding: '1px 6px',
                      }}>
                        {conf.label}
                      </span>
                      <span style={{ fontSize: '11px', color: '#888' }}>{proposal.reasoning}</span>
                    </div>

                    {/* Alternate candidates */}
                    {proposal.candidates.length > 1 && (
                      <div style={{ marginTop: '5px', display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '10.5px', color: '#aaa', alignSelf: 'center' }}>Alt:</span>
                        {proposal.candidates.slice(0, 4).map((c, ci) => (
                          <button
                            key={ci}
                            onClick={() => swapCandidate(idx, ci)}
                            title={`${c.activity_name} | ${c.location}`}
                            style={{
                              fontSize: '10.5px', padding: '2px 7px', borderRadius: '4px', cursor: 'pointer',
                              border: `1px solid ${proposal.bestMatch === c ? '#1a73e8' : '#ddd'}`,
                              background: proposal.bestMatch === c ? '#e8f0fe' : '#fff',
                              color: proposal.bestMatch === c ? '#1a73e8' : '#555',
                            }}
                          >
                            {(c.reference_product || c.activity_name).slice(0, 28)} · {c.location}
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ fontSize: '12px', color: '#c62828', fontStyle: 'italic' }}>
                    No match found in database
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer hint */}
      <div style={{ padding: '8px 16px', background: '#fafafa', fontSize: '11px', color: '#aaa', borderTop: '1px solid #f0f0f0' }}>
        Check each match before applying. Low-confidence items may need manual review in the LCI Inputs table.
      </div>
    </div>
  );
}
